/**
 * Every project, and where its intake stands.
 *
 * The list a designer lands on. Rows are one line of status each — a project
 * whose link has never been opened says so, because chasing the expert is the
 * next action and the list is where that is decided.
 */

import { useEffect, useState } from 'react';
import { ds } from '../ds';
import { Link } from '../router';
import { api, ApiError, type ProjectSummary } from '../api';
import {
  RADIUS,
  display,
  formatMeeting,
  formatWhen,
  mono,
  monoPlain,
  panel
} from '../theme';

const STATUS: Record<ProjectSummary['status'], string> = {
  waiting: 'Link not opened yet',
  opened: 'Opened, not started',
  in_progress: 'In progress',
  submitted: 'Brief ready'
};

export function Submissions() {
  const { Badge, Button } = ds();
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const res = await api.projects.list();
        setProjects(res.projects);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Could not load your projects.');
      }
    })();
  }, []);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '36px 48px 140px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 24,
          flexWrap: 'wrap',
          marginBottom: 28
        }}
      >
        <div>
          <div style={{ ...mono, marginBottom: 12 }}>Scope</div>
          <h1 style={display(40)}>Submissions</h1>
        </div>
        <Link to="/new">
          <Button variant="primary" size="md">
            New project
          </Button>
        </Link>
      </div>

      {error && <p style={{ fontSize: 14, color: 'var(--danger-500)' }}>{error}</p>}

      {projects && projects.length === 0 && (
        <div style={{ ...panel, padding: '48px 52px', maxWidth: 620 }}>
          <h2 style={display(26)}>Nothing here yet.</h2>
          <p style={{ margin: '10px 0 22px', fontSize: 15, lineHeight: 1.6, color: 'var(--text-muted)' }}>
            Create one to send your first intake link.
          </p>
          <Link to="/new">
            <Button variant="primary" size="md">
              New project
            </Button>
          </Link>
        </div>
      )}

      {projects && projects.length > 0 && (
        <div style={{ ...panel, padding: '8px 0' }}>
          {projects.map((p) => (
            <Link key={p.id} to={`/p/${p.id}`}>
              <div
                className="mv-row"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 20,
                  padding: '18px 28px',
                  borderTop: '1px solid var(--border-subtle)',
                  transition: 'var(--transition-control)',
                  flexWrap: 'wrap'
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-strong)' }}>
                      {p.title}
                    </span>
                    {p.unread > 0 && (
                      <Badge tone="signal" dot>
                        New
                      </Badge>
                    )}
                    {p.state === 'archived' && <Badge tone="neutral">Archived</Badge>}
                  </div>
                  <div style={monoPlain}>
                    {p.smeName}
                    {p.team ? ` · ${p.team}` : ''} ·{' '}
                    {p.submittedAt ? `submitted ${formatWhen(p.submittedAt)}` : STATUS[p.status]}
                    {p.meetingAt ? ` · meeting ${formatMeeting(p.meetingAt)}` : ''}
                  </div>
                </div>
                <span
                  style={{
                    ...mono,
                    fontSize: 9,
                    padding: '5px 10px',
                    borderRadius: RADIUS.pill,
                    background: p.submittedAt ? 'var(--primary-soft)' : 'transparent',
                    color: p.submittedAt ? 'var(--primary-text)' : 'var(--text-quiet)',
                    border: p.submittedAt ? 'none' : '1px solid var(--border-subtle)'
                  }}
                >
                  {STATUS[p.status]}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
